import {externalLinkTypes} from '@/consts/externalLinkTypes';
import UWM_COMMAND_TYPES from '@/consts/uwm/uwmCommandTypes';
import appErrorService from '@/services/app/appErrorService';
import iccMarketService from '@/services/icc/iccMarketService';
import iccSsoService from '@/services/icc/iccSsoService';
import log from '@/services/logger/log';
import {
    resolveSsoLinkUrlForOpenFromState,
    shouldRequestSessionInfo,
} from '@/services/sso/ssoIccSessionInfoPolicy.wired';
import uwmService from '@/services/uwm/uwmService';
import {makeSelectExternalLink} from '@/state/selectors/consumer';
import {selectLanguageCode} from '@/state/selectors/global';
import {makeSelectSsoLink} from '@/state/selectors/media';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch, getState} from '@/state/store';

const getExternalLinks = async (externalLinks, isRefetch) => {
    try {
        if (!externalLinks?.length) return;

        if (isRefetch) {
            dispatch(showLoader());
        }

        const externalLinkCodes = [];
        const ssoTemplates = [];
        const urls = [];

        externalLinks.forEach((extLink) => {
            addExternalLink(extLink, externalLinkCodes, ssoTemplates, urls);
        });

        for (const externalLinkCode of externalLinkCodes) {
            await iccMarketService.getExternalLink(externalLinkCode);
        }

        await iccSsoService.getSsoLinks({
            ssoTemplates,
            urls,
            includeSessionInfo: shouldRequestSessionInfo({
                allowSessionInfo: true,
            }),
        });

        //last state should be used
        const state = getState();
        const externalLinksResult = [];
        const languageCode = selectLanguageCode(state);

        externalLinks.forEach((extLink) => {
            const {formattedSsoTemplate} = extLink || {};
            let result;

            if (formattedSsoTemplate) {
                const ssoEntry = makeSelectSsoLink(formattedSsoTemplate)(state);
                const hasSsoSliceData = Boolean(ssoEntry?.status) || Boolean(ssoEntry?.url);

                if (hasSsoSliceData) {
                    // UWM links are always opened in a regular tab, never Bluefy
                    result = {
                        ...ssoEntry,
                        link: resolveSsoLinkUrlForOpenFromState(ssoEntry, false) ?? ssoEntry.url,
                    };
                }
            }

            if (!result) {
                result = makeSelectExternalLink(formattedSsoTemplate, languageCode)(state);
            }

            if (!result) return;

            externalLinksResult.push({template: formattedSsoTemplate, externalLinkResult: result});
        });

        // If this is a refetch attempt and we still don't have all requested links
        // or any SSO link is explicitly marked as failed, show a global error.
        const hasFailedSsoLinks = externalLinksResult.some(({externalLinkResult}) =>
            iccSsoService.isFailed(externalLinkResult?.status)
        );

        if (isRefetch && (externalLinksResult.length !== externalLinks.length || hasFailedSsoLinks)) {
            appErrorService.showGlobalError();
        }

        postExternalLinksResult(externalLinksResult);
    } catch (error) {
        log.info(`Get external link from UWM error ${error}`);
        if (isRefetch) {
            appErrorService.showGlobalError();
        }
    } finally {
        if (isRefetch) {
            dispatch(hideLoader());
        }
    }
};

const addExternalLink = (extLink, externalLinkCodes, ssoTemplates, urls) => {
    const {externalLink, params} = extLink || {};

    if (!externalLink) return;

    const {linkType, ssoTemplate, externalLinkCode, ssoUrl} = externalLink || {};
    const isExternalLinkCode =
        (externalLinkTypes.isUrl(linkType) || externalLinkTypes.isUrlInApp(linkType)) && externalLinkCode;
    const formattedSsoTemplate = iccSsoService.getFormattedSsoTemplate({ssoTemplate, params});
    const formattedSsoUrl = iccSsoService.getFormattedSsoTemplate({ssoTemplate: ssoUrl, params});

    if (isExternalLinkCode) {
        externalLinkCodes.push(externalLinkCode);
    } else {
        if (formattedSsoTemplate) {
            ssoTemplates.push(formattedSsoTemplate);
        }
        if (formattedSsoUrl) {
            urls.push(formattedSsoUrl);
        }
    }
};

const postExternalLinksResult = (externalLinks) => {
    if (!externalLinks?.length) return;

    uwmService.postMessage({
        command: UWM_COMMAND_TYPES.UWM_INIT_EXTERNAL_LINKS,
        payload: {
            externalLinks,
        },
    });
};

export default {
    getExternalLinks,
};
